"use client";

import { useState, useRef, useCallback, useEffect } from "react";

/**
 * Manages a one-shot glow animation that starts immediately and expires after
 * `initialDurationMs`. Once expired, glowing never restarts (unless the component
 * remounts). Calling `extendGlow(ms)` resets the expiry timer — but only while
 * the glow is still active.
 */
export function useGlow(initialDurationMs: number) {
  const [isGlowing, setIsGlowing] = useState(true);
  const expired = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleEnd = useCallback((delayMs: number) => {
    if (expired.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      expired.current = true;
      setIsGlowing(false);
    }, delayMs);
  }, []);

  useEffect(() => {
    scheduleEnd(initialDurationMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Extend the active glow by `extraMs` from now. No-op if already expired. */
  const extendGlow = useCallback((extraMs = 2000) => {
    scheduleEnd(extraMs);
  }, [scheduleEnd]);

  return { isGlowing, extendGlow };
}
